import page from 'page/page.mjs'; // https://visionmedia.github.io/page.js/
// Other libs that looked interesting (but didn't use):
//  https://github.com/arboleya/ways
//  https://github.com/TehShrike/abstract-state-router
//  https://github.com/foxdonut/meiosis/tree/master/helpers/routing
//  https://github.com/andreasbm/web-router

import { url as urlTool } from '../utility/tools/url.js';
import { log } from './log.js';

const URLPARAM_RETURNURL = 'returnurl';

/**
 * lag.app.router:
 *      init([settings])
 *      start()
 *      stop()
 *
 *      onLoad(path, ...callbacks)
 *      onLoadReplace(path, replacementPath)
 *      onExit(path, ...callbacks)
 *
 *      current(path)
 *      getReturnPath()
 *      
 *      goto(path, [returnPath])
 *      return([fallbackPath])
 *      back([fallbackPath], [state])
 *      
 *      replace(replacementPath, [noRouting])
 *      push(path, [state])
 *      pushback(path, [state])
 *
 * callback(context):
 *      context.handled         // bool[false]: set as true to indicate route has been handled,
 *                                 if false and final callback calls next() then a final/default callback will be invoked which does a GET request for the page
 *      context.title           // history state's page title
 *      context.state           // history state's data
 *      context.save()          // save/replace history state with whatever is in context.state (uses history.replaceState)
 *      context.canonicalPath   // full relative path with base, eg: "/base/some/path?with=query"
 *      context.path            // full relative path, eg: "/some/path?with=query"
 *      context.pathname        // relative path, eg: "/some/path"
 *      context.querystring     // query string, eg: "with=query"
 *      context.query           // parsed query string, eg: { with: "query" } // this functionality is not native to page.js, it is added in our custom router.init
 *      context.<others>        // other properties can be added to context as a way to pass values to subsequent callbacks for the current route execution
 */
export const router = {
    /**
     * Initialize page.js with optional settings, by default page.js default settings are used, in addition to a
     * settings.base property which specifies the root of the app (this defaults to '/').
     * After initializing settings this init call will start the page.js link interception/handling,
     * and wire-up our default route handling 'middleware' which adds query-string auto-parsing into context.query properties.
     * @param {Object} [settings=null]
     */
    init(settings) {
        settings = {
            base: '',
            ...settings
        };

        // Init/start page.js system.
        page.base(settings.base);
        page(settings);

        // Default handling/functionality executed for all routes.
        page('*', (ctx, next) => {
            // Try to typify (eg into Numbers) values, otherwise leaves as strings.
            _tryTypifyProperties(ctx.params);

            next(); // Continue executing any registered route callbacks.
        });

        return this;
    },
    start() {
        page.start();
        return this;
    },
    stop() {
        page.stop();
        return this;
    },

    // == Route Configuration Methods =========================================

    /**
     * When the specified route path is loaded then the callback(s) will be executed. Callbacks are added in the order they are added (whether in single or multiple calls to onLoad).
     * @param {String|Object} path - Usually a path for the route, but can be a collection of { <path>:<callbacks>, ... } instead.
     * @param {...Function} callbacks - Single/multiple callbacks to add for the path. Must not be specified if path is a collection (object) of path/callbacks.
     *      Callbacks will be passed a 'context' argument containing information about the request, including:
     *          params: A collection of the variable segements in the path (URL) pattern, eg 'some/:id/view' would parse out the ':id' segment and place it in the params.id property.
     *          query: A collection of the URL query string parameters (ie the stuff after the ? mark), these are named as per their query string naming.
     *      Callbacks can return true/other, false, or a Promise:
     *          true/other: Continue executing callbacks for this route.
     *          false: Dont execute any more callabacks for this route, don't run default (href) action either.
     *          Promise: Wait for promise to resolve (if it does) and then use its resolved value as the callback return value (with same logic as stated here/above).
     */
    onLoad(path, ...callbacks) {
        if (!path || !path.length)
            throw new Error(`lag.app.router.onLoad{missing-path}`);

        // If collection (object) of path/callbacks then loop them and add appropriately
        if (typeof path === 'object') {
            if (callbacks)
                throw new Error(`lag.app.router.onLoad{single-arg-only-for-multipath}`);

            for (const k in path) {
                if (path.hasOwnProperty(k))
                    this.onLoad(k, path[k]);
            }
        }
        else if (typeof path !== 'string')
            throw new Error(`lag.app.router.onLoad{path-type-invalid}`);
        else if (!callbacks || !callbacks.length)
            throw new Error(`lag.app.router.onLoad{callbacks-invalid}`);
        else {
            callbacks = _wrapCallbacks(callbacks);
            // Add the wrapped callbacks to the handled routes.
            page(path, ...callbacks);
        }

        return this;
    },
    /**
     * When the specified route path is loaded then replace the current route path with the replacement one (aka redirect).
     * @param {String} path - Replacement/redirect is triggered on this path match.
     * @param {String} replacementPath - The replacement path that will be loaded.
     */
    onLoadReplace(path, replacementPath) {
        if (!path || !path.length)
            throw new Error(`lag.app.router.onLoadReplace{missing-path}`);
        if (!replacementPath || !replacementPath.length)
            throw new Error(`lag.app.router.onLoadReplace{missing-replacementPath}`);

        // Setup a redirection route.
        page.redirect(path, replacementPath);

        return this;
    },
    /**
     * When the specified route path is exited (was loaded and then leaving to go elsewhere) then the callback(s) will be executed.
     * @param {String} path - See router.onLoad path info for details.
     * @param {...Function} callbacks - See router.onLoad callbacks info for details.
     */
    onExit(path, ...callbacks) {
        if (!path || !path.length)
            throw new Error(`lag.app.router.onExit{missing-path}`);

        // If collection (object) of path/callbacks then loop them and add appropriately
        if (typeof path === 'object') {
            if (callbacks)
                throw new Error(`lag.app.router.onExit{single-arg-only-for-multipath}`);

            for (const k in path) {
                if (path.hasOwnProperty(k))
                    this.onExit(k, path[k]);
            }
        }
        else if (typeof path !== 'string')
            throw new Error(`lag.app.router.onExit{path-type-invalid}`);
        else if (!callbacks || !callbacks.length)
            throw new Error(`lag.app.router.onExit{callbacks-invalid}`);
        else {
            callbacks = _wrapCallbacks(callbacks);
            // Add the wrapped callbacks to the handled routes.
            page.exit(path, ...callbacks);
        }

        return this;
    },

    // == Informational Methods ===============================================

    /**
     * Get or check the current route's path. Useful to know if your route is currently active.
     * Depending on path argument the return value is either a regex match object or a boolean.
     * @param {String|RegExp} [path=null]
     *      - If falsy (default) then the current route (with query string) will be returned (String).
     *      - If regex then it will be matched against the current route and returns a bool indicating match or not, same as regex.test(router.current()).
     *      - If string with ? or # then it will do an exact match against the current route path (with query string).
     *      - If string without ? or # then it will do an exact match against the current route path (without query string).
     * @returns {String|Boolean}
     *      - If no param (or falsy) was provided for path then the current route's path (with query string) will be returned.
     *      - If a string or regex was provided for path then a bool will be returned indicating if it was a match.
     */
    current(path) {
        if (!path)
            return page.current;
        else if (path instanceof RegExp)
            return path.test(page.current);

        // Should always start with a slash, cause exception here so we know if someone has mistakenly forgotten the slash.
        if (!path.startsWith('/'))
            throw new Error('lag.app.router.current{path-not-rel}:' + path);

        if (path.indexOf('?') >= 0 || path.indexOf('#') >= 0)
            return !!page.current.match(path);
        else { // Match path without query string or hash.
            let end = page.current.indexOf('?');
            if (end < 0) end = page.current.indexOf('#');
            if (end < 0) end = page.current.length;

            const rxPath = new RegExp(`^${path}$`);
            return rxPath.test(page.current.substring(0, end));
        }
    },
    getReturnPath(fallback) {
        return _getReturnPath() || fallback || '/';
    },

    // == Navigation Methods ==================================================

    /**
     * Navigate is a convenience method to do a 'hard' navigate, ie location.href=url rather than router.goto,
     * this doesn't have anything to do with the router system, it is simply for convenience, esp for returnurl usage.
     * @param {any} url
     * @param {any} returnurl - If window.location is provided then it will auto format the current relative url (and search query).
     */
    navigate(url, returnPath) {
        if (returnPath === window.location) {
            returnPath = window.location.pathname + window.location.search;
        }
        const params = returnPath ? { [URLPARAM_RETURNURL]: returnPath } : undefined;
        const newUrl = urlTool.assemble(url, params);
        if (window.location.href === newUrl)
            log.error('router.navigate:same-url', newUrl);
        else
            window.location.href = newUrl;
    },
    /**
     * Go to the specified route path.
     * @param {String} path - Path to load.
     * @param {String|Boolean} [returnPath] - Return path to add as a query parameter to path (?returnurl=<returnPath>),
     *                                        or true to auto use the current route as the return path (only use this if you want to add on history entries rather than having the page use the history.back functionality),
     *                                        falsy means no return path should be added (default).
     */
    goto(path, returnPath) {
        if (returnPath) {
            if (returnPath === true) {
                returnPath = page.current;
            }

            // Add the return path as a url param (?returnurl=<path>), clean it to ensure same origin.
            path = urlTool.assemble(path, { [URLPARAM_RETURNURL]: _cleanReturnPath(returnPath) });
        }
        // else no return path.

        if (!path.startsWith('/'))
            throw new Error(`lag.app.router.goto{path-not-rel}:${path}`);

        page.show(path);
    },
    /**
     * Go to the return path if one exists (query param ?returnurl=...), otherwise execute back(fallbackPath) functionality.
     * Note: if the return path is for a different origin then it will be ignored, this is for security (phishing prevention) reasons.
     * @param {String} fallbackPath
     */
    return(fallbackPath) {
        // Make sure a fallback is specified and it starts with a slash.
        // Issues can occur if no fallback specified as then the route can default to "" (blank)
        // and history pushState can fail even though the pagejs route will be activated.
        if (!fallbackPath || !fallbackPath.length || !fallbackPath.startsWith('/'))
            throw new Error('adminapp/app/router.return{invalid-fallbackPath}');

        const returnPath = _getReturnPath();

        if (returnPath) page.show(returnPath);
        else page.back(fallbackPath);
    },
    /**
     * NOTICE: You probably want to use return(...) instead so as to get the special return path functionality.
     * This call will simply go to the previous entry in the browser history (for this app's routing session),
     * if there isn't any more history entries or the history entries are prior to the current routing 'session'
     * (ie on a different origin/app, or a fresh reload was executed in-between) then it will use the fallbackPath instead.
     * When the fallbackPath is used it will goto the path, which will add it as a history entry.
     * If you want to go back to the previous history entry no-matter-what (even if on a different origin) then use history.back() instead.
     * @param {String} [fallbackPath=null]
     * @param {Object} [fallbackState]
     */
    back(fallbackPath, fallbackState) {
        // Make sure a fallback is specified and it starts with a slash.
        // Issues can occur if no fallback specified as then the route can default to "" (blank)
        // and history pushState can fail even though the pagejs route will be activated.
        if (!fallbackPath || !fallbackPath.length || !fallbackPath.startsWith('/'))
            throw new Error('adminapp/app/router.back{invalid-fallbackPath}');

        page.back(fallbackPath, fallbackState);
    },

    // == History Modification Methods ========================================

    /**
     * Replaces the current route with the new route (and loads the new route as normal, triggering any appropriate callbacks).
     * Similar to history.replaceState except will trigger callbacks appropriately, if you don't want callbacks to trigger then
     * you can specify true for the second parameter and it will simply call history.replaceState directly to modify history.
     * @param {String} replacementPath - Path that will replace the current route path.
     * @param {Boolean} [noRouting=false] - If routing callbacks should NOT be called, default is to execute any route callbacks as normal.
     */
    replace(replacementPath, noRouting) {
        if (noRouting)
            history.replaceState(null, replacementPath, replacementPath);
        else
            page.redirect(replacementPath); // When called with single arg it will do an immediate redirect (route replacement).
    },
    /**
     * Convenience method for history.pushState which adds/injects a history entry.
     * Unlike the replace method this push method will ADD a history entry rather than REPLACE the current entry.
     * It will NOT trigger callbacks, if you want it to trigger callbacks then you should probably be using goto(...) method instead.
     * If this is called from within a route callback it will add the specified path BEFORE the excuting route's path is added into the history.
     * @param {String} path
     * @param {Object} [state] - Optional history state data.
     */
    push(path, state) {
        history.pushState(state, path, path);
    },
    /**
     * Functionally similar to the push(...) method except effectively "pushes" the path into the history immediatley before the current page.
     * This is accomplished by replacing the current history entry with the new path/state and then pushing a new entry with the actual current path/state.
     * @param {String} path - The path to "push" into the previous (back) history entry.
     * @param {Object} [state] - Optional history state data.
     */
    pushback(path, state) {
        const currentState = history.state;
        const currentPath = page.current;

        history.replaceState(state, path, path); // Replace the current route's history entry with our 'back' path.
        history.pushState(currentState, currentPath, currentPath); // Then push the current route's entry so it is like before (but with the 'back' path before it).
    }
};

// == Private Methods =========================================================

const RX_TYPIFY_NUMBER = /^\d+\.?\d*$/;
function _tryTypifyProperties(props) {
    if (!props) return props;

    // Check if properties are parsable into Numbers.
    for (const k in props) {
        const p = props[k];

        // Only if looks like a number, otherwise we might accidentally parse something like "123abc".
        if (!p || !RX_TYPIFY_NUMBER.test(p))
            continue;

        // Try to parse as Number.
        const n = Number(p);
        // If successfully parsed as a number then replace the original (string) with the number.
        if (!isNaN(n))
            props[k] = n;
    }
}

function _wrapCallbacks(callbacks) {
    if (!callbacks || !callbacks.length)
        throw new Error(`lag.app.router._wrapCallbacks{no-callbacks}`);

    const wrappedCallbacks = [];

    for (let i = 0; i < callbacks.length; ++i) {
        const f = callbacks[i];

        if (typeof f !== 'function')
            throw new Error(`lag.app.router._wrapCallbacks{callback-not-func}`);

        // Wrap the provided callback function so that 'next' (the next callback) is automatically executed
        // unless an explicit false is returned by the callback, or if the callback returns a Promise then only execute 'next' once that has completed.
        wrappedCallbacks.push((context, next) => {
            const result = f(context); // We only pass the context to the callback, not the 'next' function as page.js would normally have (which we deal with below).
            _wrapCallbacks_next(result, next);
        });
    }

    return wrappedCallbacks;
}

function _wrapCallbacks_next(result, next) {
    if (result !== false) {
        // If a promise then defer until the promise resolves,
        // then use the promise's result instead (recursive).
        if (result instanceof Promise)
            result.then(r => _wrapCallbacks_next(r));
        else
            next(); // Not false and not a Promise so continue to next callback (if one exists).
    }
    // Otherwise if result IS false (explicitly false, not just falsy) then we DONT continue executing callbacks.
}

function _getReturnPath() {
    // Grab ?returnurl param (if exists).
    const path = urlTool.getParam(URLPARAM_RETURNURL);

    // Fail, no return path exists.
    if (!path || !path.length)
        return;

    // Fail, return path invalid (must be relative path for security reasons).
    if (path.indexOf('//') >= 0) // Absolute path, which we don't want, so ignore.
        return;

    return path;
}

function _cleanReturnPath(path) {
    if (!path || !path.length)
        return;
    else
        path = path.trim();

    // If absolute URL then we need to ensure it is same as current (app) origin, for security reasons.
    // Also we need to turn it into a relative url for consistency and security.
    const matches = path.match(/(https?:\/\/[^/]+)\/(.*)/i); // Is absolute url, no trailing slash necessary.
    if (matches) {
        const origin = matches[1];
        if (origin !== document.location.origin)
            return; // Fail, different origin.

        path = matches[2].trim(); // Path & query, without origin (ie relative).

        // If the app is being hosted within a sub-dir (eg /projects/myapp) then we need to accomodate for that.
        const base = page.base();
        if (base && base.length) {
            path = path.replace(new RegEx(`^${base}`), ''); // Remove base from start of path (if it exists).
        }
    }

    if (path && path.length)
        return path;
    // else no good path.
}